import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface AgentNotification {
  id: string;
  type: "insight" | "reminder" | "alert" | "suggestion";
  title: string;
  message: string;
  priority: "low" | "medium" | "high" | "critical";
  action_url?: string;
  action_data?: Record<string, unknown>;
  read_at?: string;
  dismissed_at?: string;
  created_at: string;
}

interface AgentSuggestion {
  type: string;
  title: string;
  description: string;
  priority: "low" | "medium" | "high";
  actionData?: Record<string, unknown>;
}

interface AgentResponse {
  response: string;
  actions: Array<{
    type: string;
    description: string;
    data?: Record<string, unknown>;
  }>;
  memories: Array<{
    key: string;
    value: string;
    type: string;
  }>;
  confidence: number;
}

interface AgentContextType {
  // State
  notifications: AgentNotification[];
  unreadCount: number;
  isThinking: boolean;
  sessionId: string;

  // Core agent functions
  think: (message: string, context?: Record<string, unknown>) => Promise<AgentResponse | null>;
  remember: (key: string, value: unknown, type?: string) => Promise<boolean>;
  recall: (key?: string, type?: string) => Promise<Record<string, unknown>[]>;
  getSuggestions: (tableId?: string) => Promise<AgentSuggestion[]>;

  // Interaction logging
  logInteraction: (
    action: string,
    input?: Record<string, unknown>,
    output?: Record<string, unknown>,
    outcome?: string,
    durationMs?: number
  ) => Promise<string | null>;
  recordFeedback: (interactionId: string, feedback: "positive" | "negative" | "neutral") => Promise<void>;

  // Notification management
  markAsRead: (notificationId: string) => Promise<void>;
  dismissNotification: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const AgentProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AgentNotification[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());

  const unreadCount = notifications.filter((n) => !n.read_at && !n.dismissed_at).length;

  // Fetch notifications on mount
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    const fetchNotifications = async () => {
      const { data } = await supabase
        .from("agent_notifications")
        .select("*")
        .eq("user_id", user.id)
        .is("dismissed_at", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (data) {
        setNotifications(data as AgentNotification[]);
      }
    };

    fetchNotifications();

    // Subscribe to real-time notifications
    const channel = supabase
      .channel("agent_notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "agent_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as AgentNotification, ...prev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Core agent think function
  const think = useCallback(
    async (message: string, context?: Record<string, unknown>): Promise<AgentResponse | null> => {
      if (!user) return null;

      setIsThinking(true);
      try {
        const { data, error } = await supabase.functions.invoke("agent-think", {
          body: {
            operation: "think",
            message,
            context,
          },
        });

        if (error) {
          console.error("Agent think error:", error);
          return null;
        }

        return data as AgentResponse;
      } catch (err) {
        console.error("Agent think error:", err);
        return null;
      } finally {
        setIsThinking(false);
      }
    },
    [user]
  );

  // Remember a fact/preference
  const remember = useCallback(
    async (key: string, value: unknown, type = "fact"): Promise<boolean> => {
      if (!user) return false;

      try {
        const { error } = await supabase.functions.invoke("agent-think", {
          body: {
            operation: "remember",
            key,
            value,
            memoryType: type,
          },
        });

        return !error;
      } catch {
        return false;
      }
    },
    [user]
  );

  // Recall memories
  const recall = useCallback(
    async (key?: string, type?: string): Promise<Record<string, unknown>[]> => {
      if (!user) return [];

      try {
        const { data, error } = await supabase.functions.invoke("agent-think", {
          body: {
            operation: "recall",
            key,
            memoryType: type,
          },
        });

        if (error) return [];
        return data?.memories || [];
      } catch {
        return [];
      }
    },
    [user]
  );

  // Get proactive suggestions
  const getSuggestions = useCallback(
    async (tableId?: string): Promise<AgentSuggestion[]> => {
      if (!user) return [];

      try {
        const { data, error } = await supabase.functions.invoke("agent-think", {
          body: {
            operation: "suggest",
            tableId,
          },
        });

        if (error) return [];
        return data?.suggestions || [];
      } catch {
        return [];
      }
    },
    [user]
  );

  // Log an interaction for learning
  const logInteraction = useCallback(
    async (
      action: string,
      input?: Record<string, unknown>,
      output?: Record<string, unknown>,
      outcome?: string,
      durationMs?: number
    ): Promise<string | null> => {
      if (!user) return null;

      try {
        const { data, error } = await supabase.functions.invoke("agent-think", {
          body: {
            operation: "log_interaction",
            sessionId,
            action,
            input,
            output,
            outcome,
            durationMs,
          },
        });

        if (error) return null;
        return data?.interactionId || null;
      } catch {
        return null;
      }
    },
    [user, sessionId]
  );

  // Record feedback on an interaction
  const recordFeedback = useCallback(
    async (interactionId: string, feedback: "positive" | "negative" | "neutral"): Promise<void> => {
      if (!user) return;

      try {
        await supabase.functions.invoke("agent-think", {
          body: {
            operation: "learn",
            interactionId,
            feedback,
          },
        });
      } catch (err) {
        console.error("Failed to record feedback:", err);
      }
    },
    [user]
  );

  // Notification management
  const markAsRead = useCallback(
    async (notificationId: string): Promise<void> => {
      if (!user) return;

      await supabase
        .from("agent_notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .eq("user_id", user.id);

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n))
      );
    },
    [user]
  );

  const dismissNotification = useCallback(
    async (notificationId: string): Promise<void> => {
      if (!user) return;

      await supabase
        .from("agent_notifications")
        .update({ dismissed_at: new Date().toISOString() })
        .eq("id", notificationId)
        .eq("user_id", user.id);

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    },
    [user]
  );

  const markAllAsRead = useCallback(async (): Promise<void> => {
    if (!user) return;

    await supabase
      .from("agent_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);

    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
  }, [user]);

  return (
    <AgentContext.Provider
      value={{
        notifications,
        unreadCount,
        isThinking,
        sessionId,
        think,
        remember,
        recall,
        getSuggestions,
        logInteraction,
        recordFeedback,
        markAsRead,
        dismissNotification,
        markAllAsRead,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error("useAgent must be used within AgentProvider");
  }
  return context;
};
